import { useState, useRef, useEffect } from 'react'
import { careerApply, publishResume, updatePublishedResume } from '../utils/api'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  surface2:  'rgba(255,255,255,0.05)',
  border:    'rgba(180,185,230,0.09)',
  borderHi:  'rgba(180,185,230,0.22)',
  accent:    '#b8bdd4',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  textDim:   '#6a6e85',
  green:     '#4dc88a',
  orange:    '#c8964d',
  red:       '#c85a5a',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

function Label({ children }) {
  return (
    <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 12 }}>
      {children}
    </div>
  )
}

function Section({ title, children, style = {} }) {
  return (
    <div style={{ marginBottom: 28, ...style }}>
      <Label>{title}</Label>
      {children}
    </div>
  )
}

function ScoreBar({ score }) {
  const color = score >= 80 ? C.green : score >= 60 ? C.accent : C.orange
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
      <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
      <span style={{ fontFamily: font.mono, fontSize: 10, color, minWidth: 30 }}>{score}%</span>
    </div>
  )
}

function LevelBadge({ level }) {
  const map = {
    strong:  { label: 'Есть',     color: C.green },
    partial: { label: 'Частично', color: C.orange },
    gap:     { label: 'Пробел',   color: C.red },
  }
  const { label, color } = map[level] || map.partial
  return (
    <span style={{
      fontFamily: font.mono, fontSize: 9, padding: '2px 8px', borderRadius: 8,
      border: `1px solid ${color}50`, color, background: color + '15',
    }}>
      {label}
    </span>
  )
}

function HRCard({ q, i, openIdx, setOpenIdx, editMode }) {
  const open = openIdx === i
  const isBasic = q.type === 'basic'
  return (
    <div
      style={{
        border: `1px solid ${open ? C.borderHi : C.border}`,
        borderRadius: 10, marginBottom: 6, overflow: 'hidden',
        background: open ? 'rgba(184,189,212,0.04)' : C.surface,
      }}
    >
      <div
        onClick={() => setOpenIdx(open ? null : i)}
        style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{
            fontFamily: font.mono, fontSize: 8, padding: '2px 7px', borderRadius: 8,
            border: `1px solid ${isBasic ? C.accent + '40' : C.orange + '40'}`,
            color: isBasic ? C.accent : C.orange,
            background: isBasic ? C.accent + '10' : C.orange + '10',
            flexShrink: 0,
          }}>
            {isBasic ? 'базовый' : 'для роли'}
          </span>
          <span
            style={{ fontFamily: font.body, fontSize: 13, color: C.text, outline: 'none' }}
            contentEditable={editMode}
            suppressContentEditableWarning
          >{q.question}</span>
        </div>
        <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${C.border}` }}>
          <p
            style={{ fontFamily: font.body, fontSize: 13, color: C.text, lineHeight: 1.7, margin: '12px 0 0', whiteSpace: 'pre-wrap', outline: 'none' }}
            contentEditable={editMode}
            suppressContentEditableWarning
          >
            {q.answer}
          </p>
        </div>
      )}
    </div>
  )
}

function DeleteBtn({ onClick, style = {} }) {
  return (
    <button
      onClick={onClick}
      title="Удалить"
      style={{
        position: 'absolute', top: 6, right: 6,
        width: 20, height: 20, borderRadius: 4,
        border: `1px solid rgba(200,90,90,0.35)`,
        background: 'rgba(200,90,90,0.12)',
        color: '#c85a5a', cursor: 'pointer',
        fontFamily: 'monospace', fontSize: 11, lineHeight: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, zIndex: 2,
        ...style,
      }}
    >
      ✕
    </button>
  )
}

// Собирает весь текст из contentEditable DOM-узлов внутри элемента по data-field
function collectEdits(containerRef) {
  if (!containerRef.current) return {}
  const result = {}
  containerRef.current.querySelectorAll('[data-field]').forEach(el => {
    result[el.dataset.field] = el.innerText
  })
  return result
}

export default function ApplicationPackage({ vacancy, userId, onClose, initialSlug = null, initialData = null, isReadOnly = false }) {
  const [loading, setLoading]     = useState(false)
  const [data, setData]           = useState(initialData)

  // При изменении initialData (например, после загрузки в App) — обновляем
  useEffect(() => { if (initialData) setData(initialData) }, [initialData])
  const [error, setError]         = useState(null)
  const [skipInfo, setSkipInfo]   = useState(null)
  const [openHR, setOpenHR]       = useState(null)
  const [copied, setCopied]       = useState(false)
  const [showLetter, setShowLetter] = useState(isReadOnly)
  const [editMode, setEditMode]   = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishedSlug, setPublishedSlug] = useState(initialSlug)
  const [publishCopied, setPublishCopied] = useState(false)
  // Keys: 'why_0', 'comp_2', 'hr_3', 'ev_1'
  const [hiddenItems, setHiddenItems] = useState(new Set())
  const contentRef = useRef(null)

  function hideItem(key) {
    setHiddenItems(prev => new Set([...prev, key]))
  }

  async function generate(force = false) {
    setLoading(true)
    setError(null)
    setSkipInfo(null)
    setHiddenItems(new Set())
    try {
      const res = await careerApply(vacancy, userId, { force })
      if (res.error) throw new Error(res.error)
      if (res.skipped) { setSkipInfo(res.preflight); return }
      setData(res)
      setPublishedSlug(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  function copyText(text) {
    navigator.clipboard?.writeText(text).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function copyLink(url) {
    navigator.clipboard?.writeText(url).catch(() => {})
    setPublishCopied(true)
    setTimeout(() => setPublishCopied(false), 2500)
  }

  // Extract current editable content from DOM, filtering hidden items
  function extractContent() {
    if (!contentRef.current || !data) return buildContent(data)
    const edits = collectEdits(contentRef)
    const hrQuestions = [...(data.hr_questions || [])]
      .map((q, i) => ({
        ...q,
        question: edits[`hr_q_${i}`] || q.question,
        answer:   edits[`hr_a_${i}`] || q.answer,
      }))
      .filter((_, i) => !hiddenItems.has(`hr_${i}`))
    return {
      why_fits:         data.why_fits?.map((w, i) => ({ ...w, point: edits[`why_${i}`] || w.point, evidence: edits[`why_ev_${i}`] || w.evidence }))
                            .filter((_, i) => !hiddenItems.has(`why_${i}`)),
      competencies_map: data.competencies_map?.map((c, i) => ({ ...c, your_version: edits[`comp_${i}`] || c.your_version }))
                            .filter((_, i) => !hiddenItems.has(`comp_${i}`)),
      hr_questions:     hrQuestions,
      evidence:         data.evidence?.filter((_, i) => !hiddenItems.has(`ev_${i}`)),
      cover_letter:     edits['cover_letter'] || data.cover_letter,
      summary:          edits['summary'] || data.summary,
      value_pitch:      edits['value_pitch'] || data.value_pitch,
      coverage_percent: data.coverage_percent,
    }
  }

  function buildContent(d) {
    return {
      why_fits:         d.why_fits,
      competencies_map: d.competencies_map,
      hr_questions:     d.hr_questions,
      evidence:         d.evidence,
      cover_letter:     d.cover_letter,
      value_pitch:      d.value_pitch,
      coverage_percent: d.coverage_percent,
      summary:          d.summary,
    }
  }

  async function handlePublish() {
    setPublishing(true)
    try {
      const content = extractContent()
      if (publishedSlug) {
        // Update existing
        await updatePublishedResume(publishedSlug, content)
        setEditMode(false)
      } else {
        const res = await publishResume(userId, vacancy, content)
        setPublishedSlug(res.slug)
        setEditMode(false)
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setPublishing(false)
    }
  }

  function printPDF() {
    const el = document.getElementById('apply-print-area')
    if (!el) return
    const win = window.open('', '_blank')
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8">
      <title>Отклик — ${vacancy.title}</title>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=Inter:wght@400;500&family=Space+Mono&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; font-size: 13px; color: #1a1a2e; background: #fff; padding: 36px 48px; line-height: 1.6; }
        .company-header { text-align: center; border-bottom: 2px solid #b8bdd4; padding-bottom: 24px; margin-bottom: 32px; }
        .prepared-for { font-family: 'Space Mono', monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 0.2em; color: #8890aa; margin-bottom: 8px; }
        .company-name { font-family: 'Cormorant Garamond', serif; font-size: 42px; font-weight: 600; color: #1a1a2e; line-height: 1.1; }
        .vacancy-title { font-family: 'Inter', sans-serif; font-size: 14px; color: #6a6e85; margin-top: 6px; }
        h1 { font-family: 'Cormorant Garamond', serif; font-size: 26px; color: #1a1a2e; margin-bottom: 4px; }
        h2 { font-family: 'Cormorant Garamond', serif; font-size: 18px; color: #1a1a2e; margin: 28px 0 10px; border-bottom: 1px solid #e0e0ef; padding-bottom: 6px; }
        .sub { font-family: 'Space Mono', monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin-bottom: 16px; }
        .why-item { margin-bottom: 14px; }
        .why-point { font-weight: 500; font-size: 13px; color: #1a1a2e; }
        .why-ev { font-size: 12px; color: #555; margin-top: 3px; }
        .bar-wrap { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
        .bar-bg { flex: 1; height: 4px; background: #e8e8f0; border-radius: 2px; }
        .bar-fill { height: 100%; border-radius: 2px; }
        .comp-table { width: 100%; border-collapse: collapse; margin-top: 4px; }
        .comp-table th { font-family: 'Space Mono', monospace; font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; color: #888; text-align: left; padding: 4px 8px 8px; border-bottom: 1px solid #e8e8f0; }
        .comp-table td { font-size: 12px; padding: 8px; border-bottom: 1px solid #f0f0f8; vertical-align: top; }
        .badge { display: inline-block; font-family: 'Space Mono', monospace; font-size: 9px; padding: 2px 8px; border-radius: 8px; }
        .badge-strong { color: #1a7a4a; background: #e8f7f0; border: 1px solid #b2dfcc; }
        .badge-partial { color: #8a5c10; background: #fef6e8; border: 1px solid #f0d080; }
        .badge-gap { color: #8a2020; background: #fdf0f0; border: 1px solid #f0b0b0; }
        .hr-q { margin-bottom: 16px; padding: 14px; border: 1px solid #e8e8f0; border-radius: 8px; }
        .hr-q-label { font-family: 'Space Mono', monospace; font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin-bottom: 4px; }
        .hr-q-q { font-weight: 500; margin-bottom: 8px; }
        .hr-q-a { color: #333; white-space: pre-wrap; }
        .evidence-item { padding: 10px 14px; border-left: 3px solid #b8bdd4; margin-bottom: 10px; background: #f8f8fc; }
        .evidence-src { font-family: 'Space Mono', monospace; font-size: 9px; color: #888; margin-bottom: 3px; }
        .letter { white-space: pre-wrap; line-height: 1.8; color: #1a1a2e; padding: 20px; border: 1px solid #e8e8f0; border-radius: 8px; }
        .summary-box { background: #f0f4ff; border: 1px solid #c8d0f0; border-radius: 8px; padding: 16px; font-size: 13px; line-height: 1.7; }
        .cost { font-family: 'Space Mono', monospace; font-size: 10px; color: #bbb; margin-top: 32px; text-align: right; }
        @media print { body { padding: 20px 28px; } }
      </style>
    </head><body>`)
    win.document.write(el.innerHTML)
    win.document.write('</body></html>')
    win.document.close()
    win.focus()
    setTimeout(() => { win.print() }, 400)
  }

  const whyFits      = data?.why_fits || []
  const competencies = data?.competencies_map || []
  const hrQuestions  = data?.hr_questions || []
  const evidence     = data?.evidence || []
  const coverLetter  = data?.cover_letter || ''
  const summary      = data?.summary || ''
  const valuePitch   = data?.value_pitch || ''
  const basicQs      = hrQuestions.filter(q => q.type === 'basic')
  const roleQs       = hrQuestions.filter(q => q.type === 'role_specific')
  const cost         = data?.usage?.cost_usd

  const publishUrl = publishedSlug ? `${window.location.origin}/r/${publishedSlug}` : null

  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${C.borderHi}`, paddingTop: 20 }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: font.heading, fontSize: 18, color: C.text }}>Пакет отклика</div>
          <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginTop: 2 }}>
            {vacancy.title} · {vacancy.company}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {data && !editMode && !isReadOnly && (
            <button
              onClick={() => setEditMode(true)}
              style={{
                fontFamily: font.mono, fontSize: 10, padding: '6px 14px', borderRadius: 8,
                border: `1px solid ${C.borderHi}`, background: 'transparent', color: C.accent, cursor: 'pointer',
              }}
            >
              ✎ Редактировать
            </button>
          )}
          {data && editMode && (
            <button
              onClick={() => { setEditMode(false); setHiddenItems(new Set()) }}
              style={{
                fontFamily: font.mono, fontSize: 10, padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${C.border}`, background: 'transparent', color: C.textDim, cursor: 'pointer',
              }}
            >
              Отмена
            </button>
          )}
          {data && (
            <button
              onClick={printPDF}
              style={{
                fontFamily: font.mono, fontSize: 10, padding: '6px 14px', borderRadius: 8,
                border: `1px solid ${C.borderHi}`, background: 'transparent', color: C.accent, cursor: 'pointer',
              }}
            >
              ↓ PDF
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              fontFamily: font.mono, fontSize: 10, padding: '6px 12px', borderRadius: 8,
              border: `1px solid ${C.border}`, background: 'transparent', color: C.textDim, cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Edit mode banner ── */}
      {editMode && (
        <div style={{
          padding: '10px 16px', borderRadius: 10, marginBottom: 20,
          background: 'rgba(184,189,212,0.07)', border: `1px solid ${C.borderHi}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ fontFamily: font.body, fontSize: 12, color: C.accent }}>
            Режим редактирования — кликайте на текст, чтобы изменить
          </span>
          <button
            onClick={handlePublish}
            disabled={publishing}
            style={{
              fontFamily: font.mono, fontSize: 10, padding: '7px 18px', borderRadius: 8,
              border: `1px solid ${C.green}60`, background: C.green + '15',
              color: C.green, cursor: publishing ? 'default' : 'pointer', flexShrink: 0,
            }}
          >
            {publishing ? '...' : (publishedSlug ? '↑ Обновить публикацию' : '↑ Опубликовать')}
          </button>
        </div>
      )}

      {/* ── Published link ── */}
      {publishedSlug && !editMode && !isReadOnly && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 20,
          background: C.green + '0d', border: `1px solid ${C.green}40`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontFamily: font.mono, fontSize: 9, color: C.green, letterSpacing: '0.12em', marginBottom: 4 }}>
              ОПУБЛИКОВАНО
            </div>
            <a
              href={publishUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontFamily: font.body, fontSize: 13, color: C.accent, textDecoration: 'none', wordBreak: 'break-all' }}
            >
              {publishUrl}
            </a>
          </div>
          <button
            onClick={() => copyLink(publishUrl)}
            style={{
              fontFamily: font.mono, fontSize: 9, padding: '6px 14px', borderRadius: 8,
              border: `1px solid ${publishCopied ? C.green + '60' : C.border}`,
              background: 'transparent', color: publishCopied ? C.green : C.textDim, cursor: 'pointer', flexShrink: 0,
            }}
          >
            {publishCopied ? '✓ скопировано' : 'копировать ссылку'}
          </button>
        </div>
      )}

      {/* Publish button (when not yet published and not in edit mode) */}
      {data && !editMode && !publishedSlug && !isReadOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button
            onClick={() => { setEditMode(true) }}
            style={{
              fontFamily: font.mono, fontSize: 10, padding: '7px 18px', borderRadius: 8,
              border: `1px solid ${C.accent}40`, background: 'rgba(184,189,212,0.06)',
              color: C.accent, cursor: 'pointer',
            }}
          >
            ↑ Опубликовать резюме
          </button>
        </div>
      )}

      {/* ── Generate button ── */}
      {!data && !loading && (
        <button
          onClick={() => generate()}
          style={{
            fontFamily: font.body, fontSize: 13, fontWeight: 500,
            padding: '10px 24px', borderRadius: 10,
            border: `1px solid ${C.borderHi}`, background: 'rgba(184,189,212,0.08)',
            color: C.accent, cursor: 'pointer', width: '100%',
          }}
        >
          ◎ Сгенерировать пакет отклика
        </button>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0', fontFamily: font.mono, fontSize: 11, color: C.textDim, letterSpacing: '0.1em' }}>
          Анализирую вакансию и профиль...
        </div>
      )}

      {error && (
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.red, padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.red}30`, background: C.red + '10' }}>
          {error}
        </div>
      )}

      {skipInfo && !data && (
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.orange, padding: '14px 16px', borderRadius: 8, border: `1px solid ${C.orange}40`, background: C.orange + '10', marginTop: 8 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Откликаться не рекомендуется</div>
          {skipInfo.reasons.filter(r => r.severity !== 'info').map((r, i) => (
            <div key={i} style={{ marginBottom: 4, lineHeight: 1.5 }}>· {r.text}</div>
          ))}
          <button
            onClick={() => generate(true)}
            style={{ marginTop: 10, fontFamily: font.mono, fontSize: 10, padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.orange}60`, background: 'transparent', color: C.orange, cursor: 'pointer' }}
          >
            Всё равно сгенерировать
          </button>
        </div>
      )}

      {/* ── Print area (hidden, rendered into PDF) ── */}
      {data && (
        <div id="apply-print-area" style={{ display: 'none' }}>
          {/* Company header for PDF */}
          <div className="company-header">
            <div className="prepared-for">Подготовлено специально для</div>
            <div className="company-name">{vacancy.company}</div>
            <div className="vacancy-title">{vacancy.title}</div>
          </div>

          <h2>Почему я подхожу</h2>
          {whyFits.map((w, i) => {
            const color = w.score >= 80 ? '#1a7a4a' : w.score >= 60 ? '#8a6010' : '#8a2020'
            return (
              <div key={i} className="why-item">
                <div className="why-point">{w.point}</div>
                <div className="why-ev">{w.evidence}</div>
                {typeof w.score === 'number' && (
                  <div className="bar-wrap">
                    <div className="bar-bg"><div className="bar-fill" style={{ width: `${w.score}%`, background: color }} /></div>
                    <span style={{ fontFamily: 'Space Mono', fontSize: 10, color }}>{w.score}%</span>
                  </div>
                )}
              </div>
            )
          })}

          <h2>Карта компетенций</h2>
          <table className="comp-table">
            <thead><tr><th>Требование</th><th>Статус</th><th>Как проявляется</th></tr></thead>
            <tbody>
              {competencies.map((c, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{c.skill}</td>
                  <td><span className={`badge badge-${c.level}`}>{c.level === 'strong' ? 'Есть' : c.level === 'partial' ? 'Частично' : 'Пробел'}</span></td>
                  <td style={{ color: '#444' }}>{c.your_version}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Ответы на вопросы HR</h2>
          {[...basicQs, ...roleQs].map((q, i) => (
            <div key={i} className="hr-q">
              <div className="hr-q-label">{q.type === 'basic' ? 'Базовый вопрос' : 'Для данной роли'}</div>
              <div className="hr-q-q">{q.question}</div>
              <div className="hr-q-a">{q.answer}</div>
            </div>
          ))}

          <h2>Доказательная база</h2>
          {evidence.map((e, i) => (
            <div key={i} className="evidence-item">
              <div className="evidence-src">{e.source}</div>
              <div>{e.text}</div>
            </div>
          ))}

          <h2>Сопроводительное письмо</h2>
          <div className="letter">{coverLetter}</div>

          <h2>Summary для HH (300–500 символов)</h2>
          <div className="summary-box">{summary}</div>

          {cost && <div className="cost">Стоимость анализа: ${cost}</div>}
        </div>
      )}

      {/* ── Visible UI ── */}
      {data && (
        <div ref={contentRef}>
          {/* ── Company header ── */}
          <div style={{
            textAlign: 'center', padding: '28px 20px 24px',
            borderBottom: `1px solid ${C.borderHi}`, marginBottom: 28,
          }}>
            <div style={{
              fontFamily: font.mono, fontSize: 9, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: C.textDim, marginBottom: 10,
            }}>
              Подготовлено специально для
            </div>
            <div style={{
              fontFamily: font.heading, fontSize: 48, fontWeight: 600,
              color: C.accent, lineHeight: 1.05, letterSpacing: '-0.01em',
            }}>
              {vacancy.company}
            </div>
            <div style={{
              fontFamily: font.body, fontSize: 13, color: C.textMuted, marginTop: 8,
            }}>
              {vacancy.title}
              {vacancy.salary ? ` · ${vacancy.salary}` : ''}
            </div>
          </div>

          {/* Что получит компания — для ЛПР */}
          {(valuePitch || editMode) && (
            <Section title={`Что получит ${vacancy.company || 'компания'}`}>
              <div
                style={{ fontFamily: font.body, fontSize: 14, color: C.text, lineHeight: 1.7, outline: 'none', borderRadius: editMode ? 4 : 0, padding: editMode ? '4px' : 0, background: editMode ? 'rgba(184,189,212,0.04)' : 'transparent' }}
                contentEditable={editMode}
                suppressContentEditableWarning
                data-field="value_pitch"
              >{valuePitch}</div>
              {data.coverage_percent >= 60 && (
                <div style={{ marginTop: 10, fontFamily: font.mono, fontSize: 10, color: C.green, letterSpacing: '0.06em' }}>
                  ✓ подтверждённый опыт по {data.coverage_percent}% требований вакансии
                </div>
              )}
            </Section>
          )}

          {/* Проверка перед откликом — только владельцу */}
          {!isReadOnly && (data.preflight || data.verification) && (
            <div style={{ marginBottom: 24, padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontFamily: font.mono, fontSize: 10, letterSpacing: '0.04em', color: C.textDim, marginBottom: 6 }}>
                {data.preflight && (
                  <span style={{ color: data.preflight.verdict === 'apply' ? C.green : C.orange }}>
                    {data.preflight.verdict === 'apply' ? '✓ откликаться' : data.preflight.verdict === 'stretch' ? '△ есть риски' : '✗ не рекомендуется'}
                  </span>
                )}
                {data.coverage_percent != null && <span>покрытие требований {data.coverage_percent}%</span>}
                {data.sources && <span>резюме {data.sources.resume_chars} зн. · фактов {data.sources.facts_used} · вакансия {data.sources.vacancy_source === 'full' ? 'полная' : 'выдержка'}{data.sources.model ? ` · ${data.sources.model === 'gemini' ? 'Gemini' : 'Sonnet'}` : ''}</span>}
                {data.verification && <span style={{ color: data.verification.status === 'ok' ? C.green : C.orange }}>{data.verification.status === 'ok' ? `проверено · исправлено ${data.verification.removed.length}` : 'проверка фактов не выполнена'}</span>}
              </div>
              {(data.preflight?.reasons || []).filter(r => r.severity !== 'info').map((r, i) => (
                <div key={i}>· {r.text}</div>
              ))}
              {data.verification?.removed?.length > 0 && (
                <details style={{ marginTop: 6 }}>
                  <summary style={{ cursor: 'pointer', color: C.textDim }}>что убрала проверка</summary>
                  {data.verification.removed.map((r, i) => (
                    <div key={i} style={{ marginTop: 4 }}>· {r.claim} <span style={{ color: C.textDim }}>— {r.reason}</span></div>
                  ))}
                </details>
              )}
            </div>
          )}

          {/* WHY FITS */}
          <Section title="Почему я подхожу">
            {whyFits.map((w, i) => {
              if (hiddenItems.has(`why_${i}`)) return null
              return (
                <div key={i} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: i < whyFits.length - 1 ? `1px solid ${C.border}` : 'none', position: 'relative' }}>
                  {editMode && <DeleteBtn onClick={() => hideItem(`why_${i}`)} />}
                  <div
                    style={{ fontFamily: font.body, fontSize: 13, color: C.text, fontWeight: 500, outline: 'none', borderRadius: editMode ? 4 : 0, padding: editMode ? '2px 4px' : 0, background: editMode ? 'rgba(184,189,212,0.06)' : 'transparent', paddingRight: editMode ? 28 : 0 }}
                    contentEditable={editMode}
                    suppressContentEditableWarning
                    data-field={`why_${i}`}
                  >{w.point}</div>
                  {w.evidence && (
                    <div
                      style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginTop: 4, lineHeight: 1.5, outline: 'none', borderRadius: editMode ? 4 : 0, padding: editMode ? '2px 4px' : 0, background: editMode ? 'rgba(184,189,212,0.04)' : 'transparent' }}
                      contentEditable={editMode}
                      suppressContentEditableWarning
                      data-field={`why_ev_${i}`}
                    >{w.evidence}</div>
                  )}
                  {typeof w.score === 'number' && <ScoreBar score={w.score} />}
                </div>
              )
            })}
          </Section>

          {/* COMPETENCIES MAP */}
          <Section title="Карта компетенций">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {competencies.map((comp, i) => {
                if (hiddenItems.has(`comp_${i}`)) return null
                return (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '1fr auto 1.4fr', gap: 12, alignItems: 'start',
                    padding: '10px 14px', borderRadius: 8, background: C.surface,
                    border: `1px solid ${C.border}`, position: 'relative',
                  }}>
                    {editMode && <DeleteBtn onClick={() => hideItem(`comp_${i}`)} />}
                    <div style={{ fontFamily: font.body, fontSize: 13, color: C.text }}>{comp.skill}</div>
                    <LevelBadge level={comp.level} />
                    <div
                      style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.5, outline: 'none', borderRadius: editMode ? 4 : 0, padding: editMode ? '2px 4px' : 0, background: editMode ? 'rgba(184,189,212,0.04)' : 'transparent', paddingRight: editMode ? 28 : 0 }}
                      contentEditable={editMode}
                      suppressContentEditableWarning
                      data-field={`comp_${i}`}
                    >{comp.your_version}</div>
                  </div>
                )
              })}
            </div>
          </Section>

          {/* HR QUESTIONS */}
          {basicQs.filter((_, i) => !hiddenItems.has(`hr_${hrQuestions.indexOf(basicQs[i])}`)).length > 0 && (
            <Section title={`Ответы HR · базовые`}>
              {basicQs.map((q) => {
                const i = hrQuestions.indexOf(q)
                if (hiddenItems.has(`hr_${i}`)) return null
                return (
                  <div key={i} style={{ position: 'relative' }}>
                    {editMode && <DeleteBtn onClick={() => hideItem(`hr_${i}`)} style={{ top: 10, right: 8, zIndex: 2 }} />}
                    <HRCard q={q} i={i} openIdx={openHR} setOpenIdx={setOpenHR} editMode={editMode} />
                  </div>
                )
              })}
            </Section>
          )}

          {roleQs.filter((q) => !hiddenItems.has(`hr_${hrQuestions.indexOf(q)}`)).length > 0 && (
            <Section title={`Ответы HR · для роли`}>
              {roleQs.map((q) => {
                const i = hrQuestions.indexOf(q)
                if (hiddenItems.has(`hr_${i}`)) return null
                return (
                  <div key={`r${i}`} style={{ position: 'relative' }}>
                    {editMode && <DeleteBtn onClick={() => hideItem(`hr_${i}`)} style={{ top: 10, right: 8, zIndex: 2 }} />}
                    <HRCard q={q} i={i} openIdx={openHR} setOpenIdx={setOpenHR} editMode={editMode} />
                  </div>
                )
              })}
            </Section>
          )}

          {/* EVIDENCE */}
          {evidence.length > 0 && (
          <Section title="Доказательная база">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {evidence.map((e, i) => {
                if (hiddenItems.has(`ev_${i}`)) return null
                return (
                  <div key={i} style={{
                    padding: '10px 14px', borderLeft: `3px solid ${C.accent}50`,
                    background: C.surface, borderRadius: '0 8px 8px 0', position: 'relative',
                  }}>
                    {editMode && <DeleteBtn onClick={() => hideItem(`ev_${i}`)} />}
                    <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                      {e.source}
                    </div>
                    <div style={{ fontFamily: font.body, fontSize: 12, color: C.text, lineHeight: 1.6 }}>{e.text}</div>
                  </div>
                )
              })}
            </div>
          </Section>
          )}

          {/* COVER LETTER */}
          <Section title="Сопроводительное письмо">
            <div style={{
              padding: '16px 20px', borderRadius: 10,
              border: `1px solid ${C.border}`, background: C.surface,
            }}>
              {!editMode && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button
                    onClick={() => setShowLetter(v => !v)}
                    style={{
                      fontFamily: font.mono, fontSize: 9, padding: '4px 12px', borderRadius: 8,
                      border: `1px solid ${C.border}`, background: 'transparent', color: C.textDim, cursor: 'pointer',
                    }}
                  >
                    {showLetter ? 'свернуть' : 'показать'}
                  </button>
                </div>
              )}
              {(showLetter || editMode) ? (
                <p
                  style={{ fontFamily: font.body, fontSize: 13, color: C.text, lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0, outline: 'none', borderRadius: editMode ? 4 : 0, padding: editMode ? '4px' : 0, background: editMode ? 'rgba(184,189,212,0.04)' : 'transparent' }}
                  contentEditable={editMode}
                  suppressContentEditableWarning
                  data-field="cover_letter"
                >
                  {coverLetter}
                </p>
              ) : (
                <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6, margin: 0 }}>
                  {coverLetter.slice(0, 200)}…
                </p>
              )}
            </div>
          </Section>

          {/* SUMMARY */}
          {!isReadOnly && (
          <Section title={`Summary для HH · ${summary.length} симв.`}>
            <div style={{
              padding: '14px 18px', borderRadius: 10,
              border: `1px solid ${C.borderHi}`, background: 'rgba(184,189,212,0.05)',
              position: 'relative',
            }}>
              <p
                style={{ fontFamily: font.body, fontSize: 13, color: C.text, lineHeight: 1.7, margin: 0, outline: 'none', borderRadius: editMode ? 4 : 0, padding: editMode ? '4px' : 0, background: editMode ? 'rgba(184,189,212,0.04)' : 'transparent' }}
                contentEditable={editMode}
                suppressContentEditableWarning
                data-field="summary"
              >
                {summary}
              </p>
              {!editMode && (
                <button
                  onClick={() => copyText(summary)}
                  style={{
                    position: 'absolute', top: 10, right: 12,
                    fontFamily: font.mono, fontSize: 9, padding: '4px 10px', borderRadius: 8,
                    border: `1px solid ${copied ? C.green + '60' : C.border}`,
                    background: 'transparent', color: copied ? C.green : C.textDim, cursor: 'pointer',
                  }}
                >
                  {copied ? '✓ скопировано' : 'копировать'}
                </button>
              )}
            </div>
          </Section>
          )}

          {/* COST + REGENERATE */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            {cost && (
              <span style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim }}>
                стоимость анализа: ${cost}
              </span>
            )}
            {!isReadOnly && (
              <button
                onClick={() => generate()}
                style={{
                  fontFamily: font.mono, fontSize: 9, padding: '5px 14px', borderRadius: 8,
                  border: `1px solid ${C.border}`, background: 'transparent', color: C.textDim, cursor: 'pointer',
                }}
              >
                пересобрать
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
